import { useCallback } from "react";

import { useMutation } from "@apollo/client";
import { useRecoilState } from "recoil";

import { AspectRatioType } from "../../gql-generated/types";
import { type LensPreviewMediaItem } from "../../shared/state";
import { processMedia } from "../api/mutation/processMedia";
import {
    type ProcessMediaMutation,
    type ProcessMediaMutationVariables,
} from "../api/mutation/processMedia.gql-generated";
import { PreviewAspectRatio } from "../models";
import { croppedAreaPixelsAtom, videoRefAtom } from "../state/cropperAtoms";
import { convertToFile } from "../utils/convertToFile";

interface CropperReturn {
    handleCrop: () => Promise<File | null | undefined>;
    loading: boolean;
}

export const useCropper = (
    previewMediaItem: LensPreviewMediaItem,
    aspectRatioType: PreviewAspectRatio,
): CropperReturn => {
    const [croppedAreaPixels] = useRecoilState(croppedAreaPixelsAtom);
    const [videoRef] = useRecoilState(videoRefAtom);
    const [processMediaMutation, { loading }] = useMutation<ProcessMediaMutation, ProcessMediaMutationVariables>(
        processMedia,
    );

    const handleCrop = useCallback(async () => {
        if (croppedAreaPixels === null || (!previewMediaItem.file && !previewMediaItem.url)) {
            return null;
        }

        const isVideo = previewMediaItem.isVideo;
        const nameParts = previewMediaItem.name.split(".");
        const mediaType = nameParts.length > 1 ? nameParts.at(-1) : undefined;
        const aspectRatio =
            aspectRatioType === PreviewAspectRatio.threeFour
                ? AspectRatioType.ThreeByFour
                : AspectRatioType.NineBySixteen;
        const buildMediaTypeForURL = mediaType ? (isVideo ? `video/${mediaType}` : `image/${mediaType}`) : "";
        const mediaSource = previewMediaItem.file ? { upload: previewMediaItem.file } : { url: previewMediaItem.url };
        const determinedMediaType = previewMediaItem.file ? previewMediaItem.file.type : buildMediaTypeForURL;
        const currentTime = { timestamp: isVideo && videoRef?.paused ? videoRef.currentTime : null };

        try {
            const { data } = await processMediaMutation({
                variables: {
                    x: croppedAreaPixels.x,
                    y: croppedAreaPixels.y,
                    width: croppedAreaPixels.width,
                    height: croppedAreaPixels.height,
                    source: mediaSource,
                    mediaType: determinedMediaType,
                    aspectRatio,
                    ...currentTime,
                },
            });
            if (!data) {
                throw new Error("Failed to process media");
            }
            return convertToFile(data);
        } catch (error) {
            console.error(error);
            throw new Error("Failed to process media");
        }
    }, [
        croppedAreaPixels,
        previewMediaItem.file,
        previewMediaItem.url,
        previewMediaItem.isVideo,
        previewMediaItem.name,
        aspectRatioType,
        videoRef?.paused,
        videoRef?.currentTime,
        processMediaMutation,
    ]);

    return {
        handleCrop,
        loading,
    };
};
